import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Check,
  Pencil,
  AlertCircle,
  Clock,
  Pause,
  Trash2,
  GripVertical,
  History,
  RotateCcw,
  Plus,
  List,
  ChevronRight,
  Loader2,
  CheckCircle2,
  User,
  Users,
  X,
} from "lucide-react";
import { cn } from "../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const LAST_TODO_LIST_KEY = "notekeeper_last_todo_list";

const CATEGORIES = [
  { key: "important", label: "Important", icon: AlertCircle, color: "text-red-500", bgColor: "bg-red-500/10" },
  { key: "normal", label: "Normal", icon: CheckCircle2, color: "text-blue-500", bgColor: "bg-blue-500/10" },
  { key: "waiting", label: "Waiting", icon: Clock, color: "text-yellow-500", bgColor: "bg-yellow-500/10" },
  { key: "paused", label: "Paused", icon: Pause, color: "text-muted-foreground", bgColor: "bg-muted/20" },
];

const CategoryButtons = ({ currentCategory, onChangeCategory, todoId }) => {
  return (
    <TooltipProvider delayDuration={0}>
      {CATEGORIES.map(cat => {
        if (cat.key === currentCategory) return null;
        const Icon = cat.icon || CheckCircle2;
        return (
          <Tooltip key={cat.key}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-7 w-7", cat.color, `hover:${cat.color}`)}
                onClick={() => onChangeCategory(todoId, cat.key)}
              >
                <Icon className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {cat.key === "normal" ? "Back to Normal" : `Move to ${cat.label}`}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </TooltipProvider>
  );
};

const SortableTodoItem = ({ todo, onComplete, onEdit, onChangeCategory, onDelete, isCommon = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(todo.text);
  const inputRef = useRef(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: todo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSaveEdit = () => {
    if (editText.trim() && editText !== todo.text) {
      onEdit(todo.id, editText);
    }
    setIsEditing(false);
  };

  const category = CATEGORIES.find(c => c.key === todo.category);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 p-3 rounded-lg border bg-card mb-2 group transition-all overflow-hidden",
        isDragging && "opacity-50 shadow-lg z-10 relative",
        category?.bgColor
      )}
      data-testid={`todo-item-${todo.id}`}
    >
      <div className="flex items-start gap-2 flex-1 min-w-0 w-full">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity mt-1 flex-shrink-0 touch-none"
        >
          <GripVertical className="w-4 h-4" />
        </button>

        <div className="flex-1 min-w-0 pt-0.5 sm:pt-0">
          {isEditing ? (
            <Input
              ref={inputRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveEdit();
                if (e.key === "Escape") setIsEditing(false);
              }}
              onBlur={handleSaveEdit}
              className="h-8 w-full"
            />
          ) : (
            <>
              <span className="text-sm block break-words whitespace-pre-wrap">{todo.text}</span>
              {isCommon && (
                <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                  {todo.created_by && (
                    <span className="flex items-center gap-1 bg-background/50 px-1.5 rounded">
                      <User className="w-3 h-3" />
                      Created by {todo.created_by}
                    </span>
                  )}
                  {todo.last_edited_by && todo.last_edited_by !== todo.created_by && (
                    <span className="flex items-center gap-1 bg-background/50 px-1.5 rounded">
                      <Pencil className="w-3 h-3" />
                      Edited by {todo.last_edited_by}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-1 w-full sm:w-auto border-t sm:border-0 border-border/50 pt-2 sm:pt-0 mt-2 sm:mt-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-green-500 hover:text-green-600 hover:bg-green-50"
                onClick={() => onComplete(todo.id)}
              >
                <Check className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Complete</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setIsEditing(true)}
              >
                <Pencil className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Edit</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <CategoryButtons 
          currentCategory={todo.category} 
          onChangeCategory={onChangeCategory} 
          todoId={todo.id} 
        />
        
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => onDelete(todo.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Delete</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

const CategorySection = ({ category, todos, onComplete, onEdit, onChangeCategory, onDelete, onReorder, hideIfEmpty = false, isCommon = false }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      onReorder(category.key, active.id, over.id);
    }
  };

  const Icon = category.icon;

  if (hideIfEmpty && todos.length === 0) return null;

  return (
    <div className="mb-6">
      <div className={cn("flex items-center gap-2 mb-3 font-medium text-sm", category.color)}>
        {Icon && <Icon className="w-4 h-4" />}
        <span>{category.label}</span>
        <Badge variant="secondary" className="ml-auto">{todos.length}</Badge>
      </div>

      <DndContext 
        sensors={sensors} 
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={todos.map(t => t.id)} strategy={verticalListSortingStrategy}>
          {todos.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-lg">
              No {category.label.toLowerCase()} tasks
            </div>
          ) : (
            todos.map(todo => (
              <SortableTodoItem
                key={todo.id}
                todo={todo}
                onComplete={onComplete}
                onEdit={onEdit}
                onChangeCategory={onChangeCategory}
                onDelete={onDelete}
                isCommon={isCommon}
              />
            ))
          )}
        </SortableContext>
      </DndContext>
    </div>
  );
};

const UnifiedHistory = ({ history, onUncomplete, isCommon = false }) => {
  if (!history || history.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No history yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {history.map(item => (
        <div
          key={item.id}
          className={cn(
            "flex items-start gap-3 p-3 rounded-lg border",
            item.deleted_at ? "bg-red-50/50 border-red-200" : "bg-green-50/50 border-green-200"
          )}
        >
          {item.deleted_at ? (
            <Trash2 className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
          ) : (
            <Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
          )}
          
          <div className="flex-1 min-w-0">
            <span className={cn("text-sm block break-words whitespace-pre-wrap", item.deleted_at ? "line-through text-muted-foreground" : "line-through")}>
              {item.text}
            </span>
            
            <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-muted-foreground">
              {item.deleted_at ? (
                <span className="flex items-center gap-1 text-red-600">
                  🗑️ Deleted by {item.deleted_by || "Unknown"}
                  <span className="text-muted-foreground ml-1">
                    {new Date(item.deleted_at).toLocaleDateString()}
                  </span>
                </span>
              ) : item.completed_at ? (
                <span className="flex items-center gap-1 text-green-600">
                  ✅ Completed by {item.completed_by || "Unknown"}
                  <span className="text-muted-foreground ml-1">
                    {new Date(item.completed_at).toLocaleDateString()}
                  </span>
                </span>
              ) : null}
            </div>
          </div>

          {!item.deleted_at && item.completed_at && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 flex-shrink-0"
              onClick={() => onUncomplete(item.id)}
              title="Restore to active"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

export default function TodoPage({ groupId, isGroupContext = false, isCommon = false, sidebarOpen = true, closeSidebarMobile }) {
  const [todos, setTodos] = useState({
    important: [],
    normal: [],
    waiting: [],
    paused: [],
  });
  const [history, setHistory] = useState([]);
  const [taskLists, setTaskLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [newTodo, setNewTodo] = useState("");
  const [newCategory, setNewCategory] = useState("normal");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteListConfirm, setDeleteListConfirm] = useState(null); // Adăugat state pentru confirmare ștergere listă
  const [newListDialog, setNewListDialog] = useState(false);
  const [newListName, setNewListName] = useState("");
  const inputRef = useRef(null);

  const baseUrl = isCommon ? `${API_URL}/common` : 
                  groupId ? `${API_URL}/groups/${groupId}` : 
                  API_URL;

  const forceCloseSidebar = (e) => {
    if (e) e.stopPropagation();
    if (typeof closeSidebarMobile === 'function') {
      closeSidebarMobile();
    } else {
      const menuBtn = document.getElementById("mobile-menu-btn");
      if (menuBtn) {
        menuBtn.click();
      }
    }
  };

  const fetchTodos = useCallback(async () => {
    try {
      const url = selectedList 
        ? `${baseUrl}/todos?list_id=${selectedList.id}` 
        : `${baseUrl}/todos?list_id=null`;
      const response = await axios.get(url);
      setTodos(response.data);
    } catch (error) {
      console.error("Failed to fetch todos:", error);
      toast.error("Failed to load tasks");
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl, selectedList]);

  const handleReorder = async (categoryKey, activeId, overId) => {
    let newCategoryItems;
    
    setTodos((prev) => {
      const categoryItems = [...prev[categoryKey]];
      const oldIndex = categoryItems.findIndex((t) => t.id === activeId);
      const newIndex = categoryItems.findIndex((t) => t.id === overId);
      
      newCategoryItems = arrayMove(categoryItems, oldIndex, newIndex);
      return {
        ...prev,
        [categoryKey]: newCategoryItems,
      };
    });

    try {
      const updates = newCategoryItems.map((todo, index) => ({
        id: todo.id,
        order: index,
        category: categoryKey
      }));

      const reorderUrl = isCommon ? `${baseUrl}/todos/reorder` : 
                         isGroupContext ? `${baseUrl}/todos/reorder` : 
                         `${API_URL}/todos/reorder`;

      await axios.post(reorderUrl, updates);
    } catch (error) {
      console.error("Failed to save new order:", error);
      toast.error("Could not save the new order on server");
      fetchTodos();
    }
  };

  const fetchHistory = useCallback(async () => {
    try {
      const url = selectedList 
        ? `${baseUrl}/todos/history?list_id=${selectedList.id}` 
        : `${baseUrl}/todos/history?list_id=null`;
      const response = await axios.get(url);
      setHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch history:", error);
    }
  }, [baseUrl, selectedList]);

  const fetchTaskLists = useCallback(async () => {
    try {
      const response = await axios.get(`${baseUrl}/task-lists`);
      const lists = response.data;
      setTaskLists(lists);

      const savedListId = localStorage.getItem(LAST_TODO_LIST_KEY);
      if (savedListId) {
        if (savedListId === "null") {
          setSelectedList(null);
        } else {
          const matchingList = lists.find(l => String(l.id) === savedListId);
          if (matchingList) setSelectedList(matchingList);
        }
      }
    } catch (error) {
      console.error("Failed to fetch task lists:", error);
    }
  }, [baseUrl]);

  useEffect(() => {
    fetchTodos();
    fetchHistory();
    fetchTaskLists();
  }, [fetchTodos, fetchHistory, fetchTaskLists]);

  const handleAddTodo = async (e) => {
    e.preventDefault();
    if (!newTodo.trim()) return;

    try {
      await axios.post(`${baseUrl}/todos`, {
        text: newTodo,
        category: newCategory,
        list_id: selectedList?.id || null,
      });
      setNewTodo("");
      fetchTodos();
      inputRef.current?.focus();
    } catch (error) {
      toast.error("Failed to add task");
    }
  };

  const handleComplete = async (todoId) => {
    try {
      await axios.post(`${baseUrl}/todos/${todoId}/complete`);
      fetchTodos();
      fetchHistory();
    } catch (error) {
      toast.error("Failed to complete task");
    }
  };

  const handleUncomplete = async (todoId) => {
    try {
      await axios.post(`${baseUrl}/todos/${todoId}/uncomplete`);
      fetchTodos();
      fetchHistory();
    } catch (error) {
      toast.error("Failed to restore task");
    }
  };

  const handleEdit = async (todoId, newText) => {
    try {
      await axios.put(`${baseUrl}/todos/${todoId}`, { text: newText });
      fetchTodos();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleChangeCategory = async (todoId, newCategory) => {
    try {
      await axios.put(`${baseUrl}/todos/${todoId}`, { category: newCategory });
      fetchTodos();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleDelete = async (todoId) => {
    try {
      await axios.delete(`${baseUrl}/todos/${todoId}`);
      setDeleteConfirm(null);
      fetchTodos();
      fetchHistory();
      toast.success("Task deleted");
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    try {
      await axios.post(`${baseUrl}/task-lists`, { name: newListName });
      setNewListName("");
      setNewListDialog(false);
      fetchTaskLists();
      toast.success("List created");
    } catch (error) {
      toast.error("Failed to create list");
    }
  };

  const handleDeleteList = async (listId) => {
    try {
      await axios.delete(`${baseUrl}/task-lists/${listId}`);
      
      if (selectedList?.id === listId) {
        setSelectedList(null);
        localStorage.setItem(LAST_TODO_LIST_KEY, "null");
        setTimeout(() => {
          fetchTodos();
          fetchHistory();
        }, 0);
      }
      
      setDeleteListConfirm(null); // Închide dialogul după ștergere
      fetchTaskLists();
      toast.success("List deleted");
    } catch (error) {
      toast.error("Failed to delete list");
    }
  };

  const totalTasks = Object.values(todos).flat().length;

  const handleListSelect = (list) => {
    setSelectedList(list);
    localStorage.setItem(LAST_TODO_LIST_KEY, list ? String(list.id) : "null");
    forceCloseSidebar();
  };

  return (
    <div className="h-full flex relative overflow-hidden w-full" data-testid="todo-page">
      {sidebarOpen && (
        <div 
          className="absolute inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
          onClick={forceCloseSidebar}
        />
      )}

      <div 
        className={cn(
          "w-64 border-r border-border bg-background flex flex-col h-full z-50 transition-transform duration-300 ease-in-out shrink-0 shadow-2xl md:shadow-none",
          "absolute inset-y-0 left-0 md:relative md:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="p-3 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{isCommon ? "Shared Lists" : "Task Lists"}</span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setNewListDialog(true)}
                data-testid="create-list-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 md:hidden"
                onClick={forceCloseSidebar}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 space-y-0.5">
            <div
              className={cn(
                "flex items-center gap-2 px-3 py-2.5 rounded-md cursor-pointer hover:bg-muted/50 transition-colors",
                !selectedList && "bg-primary/10 text-primary font-medium"
              )}
              onClick={() => handleListSelect(null)}
              data-testid="all-tasks-btn"
            >
              <List className="w-4 h-4 shrink-0" />
              <span className="text-sm flex-1">General Tasks</span>
              <Badge variant="secondary" className="text-xs shrink-0">{!selectedList ? totalTasks : ""}</Badge>
            </div>

            {taskLists.map(list => (
              <div
                key={list.id}
                className={cn(
                  "flex items-start gap-2 px-3 py-2.5 rounded-md cursor-pointer hover:bg-muted/50 transition-colors group",
                  selectedList?.id === list.id && "bg-primary/10 text-primary font-medium"
                )}
                onClick={() => handleListSelect(list)}
                data-testid={`task-list-${list.id}`}
              >
                <ChevronRight className="w-4 h-4 shrink-0 mt-0.5" />
                
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-sm break-words whitespace-pre-wrap">{list.name}</span>
                  {isCommon && list.created_by && (
                    <span className="text-[10px] text-muted-foreground truncate" title={`Created by ${list.created_by}`}>
                      {list.created_by}
                    </span>
                  )}
                </div>
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 -mr-1 shrink-0 mt-0.5"
                  onClick={(e) => { e.stopPropagation(); setDeleteListConfirm(list); }}
                >
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden relative w-full">
        <div className="border-b border-border px-4 sm:px-6 py-4 flex-shrink-0 bg-background/95 backdrop-blur-sm z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 pr-2">
              {isCommon ? <Users className="w-5 h-5 text-primary flex-shrink-0" /> : <List className="w-5 h-5 text-primary flex-shrink-0" />}
              <h1 className="text-lg sm:text-xl font-semibold truncate">
                {selectedList ? selectedList.name : (isCommon ? "Shared General Tasks" : "General Tasks")}
              </h1>
              {isCommon && <Badge variant="secondary" className="hidden sm:inline-flex flex-shrink-0">Shared</Badge>}
            </div>
            <Button
              variant={showHistory ? "default" : "outline"}
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              className="flex-shrink-0"
            >
              <History className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">History</span>
            </Button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden relative">
          <ScrollArea className="flex-1">
            <div className="max-w-3xl mx-auto p-4 sm:p-6 pb-24 w-full">
              <form onSubmit={handleAddTodo} className="flex flex-col sm:flex-row gap-2 mb-8">
                <Input
                  ref={inputRef}
                  value={newTodo}
                  onChange={(e) => setNewTodo(e.target.value)}
                  placeholder={isCommon ? "Add a shared task..." : "Add a new task..."}
                  className="flex-1 h-10"
                />
                <div className="flex gap-2">
                  <select
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="h-10 px-3 py-2 border border-input rounded-md bg-background text-sm flex-1 sm:flex-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.key} value={cat.key}>{cat.label}</option>
                    ))}
                  </select>
                  <Button type="submit" className="h-10 px-4">
                    <Plus className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Add</span>
                  </Button>
                </div>
              </form>

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="w-full">
                  {CATEGORIES.map((cat, idx) => (
                    <CategorySection
                      key={cat.key}
                      category={cat}
                      todos={todos[cat.key] || []}
                      onComplete={handleComplete}
                      onEdit={handleEdit}
                      onChangeCategory={handleChangeCategory}
                      onDelete={(id) => setDeleteConfirm(id)}
                      onReorder={handleReorder}
                      hideIfEmpty={idx !== 1} // Ascunde tot în afară de 'normal' dacă e gol
                      isCommon={isCommon}
                    />
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>

          {showHistory && (
            <div className="w-full sm:w-80 border-l border-border bg-background/95 backdrop-blur-sm absolute right-0 top-0 bottom-0 z-[60] flex flex-col shadow-2xl md:static md:shadow-none">
              <div className="p-4 border-b border-border flex justify-between items-center bg-muted/30">
                <h3 className="font-medium flex items-center gap-2">
                  <History className="w-4 h-4 text-primary" /> Task History
                </h3>
                <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setShowHistory(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-4 pb-20">
                  <UnifiedHistory history={history} onUncomplete={handleUncomplete} isCommon={isCommon} />
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent className="w-[95vw] sm:w-full rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>Are you sure? It moves to history.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog pentru stergerea listei */}
      <AlertDialog open={!!deleteListConfirm} onOpenChange={() => setDeleteListConfirm(null)}>
        <AlertDialogContent className="w-[95vw] sm:w-full rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task List</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the list "{deleteListConfirm?.name}"? All tasks inside will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteList(deleteListConfirm.id)} className="bg-destructive hover:bg-destructive/90">Delete List</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={newListDialog} onOpenChange={setNewListDialog}>
        <DialogContent className="w-[95vw] sm:w-full rounded-xl max-w-md">
          <DialogHeader><DialogTitle>Create Task List</DialogTitle></DialogHeader>
          <div className="py-4">
            <Input value={newListName} onChange={(e) => setNewListName(e.target.value)} placeholder="List name" className="h-10" onKeyDown={(e) => e.key === "Enter" && handleCreateList()} />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setNewListDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateList}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}